// Product analytics — TWO complementary providers, one wrapper.
//
// This is a Firebase app, so we run BOTH, because they do different jobs:
//   • PostHog  — in-PRODUCT behavior: autocapture (every click/pageview with no
//     per-element wiring), session replay + heatmaps (where users stall),
//     funnels + retention + cohorts, feature flags. This is "where they click
//     and where they get stuck."
//   • GA4 / Google Analytics for Firebase — the Google-native ACQUISITION layer:
//     Firebase console dashboards, free unlimited events, raw BigQuery export
//     (no sampling — huge for a data team), Google Ads attribution, and
//     audiences. This is "where traffic comes from and campaign ROI."
// Neither alone covers both; the best-in-class setup is both, firing the SAME
// custom events through one `track()`.
//
// SAFE BY DEFAULT: each provider is independently no-op until its key exists —
// PostHog on VITE_POSTHOG_KEY, GA4 on the Firebase measurementId. Ships dark;
// each "goes live" the moment its key is present, no code change, no events in
// dev/CI/tests. Privacy: replays + autocapture mask ALL rendered text and inputs
// (essays/transcripts/chat live in divs, not <input>s, so input-only masking
// would leak them); we identify by a salted pseudonymous id (utils/analyticsId,
// fix 4 - never the raw email) and NEVER send transcript/essay/chat text as
// event props.

let ph = null;              // loaded posthog instance, or null when disabled
let phEnabled = false;
let ga = null;              // firebase analytics instance, or null when disabled
let gaLog = null;           // firebase/analytics logEvent, bound lazily
let gaSetUserId = null;
let gaSetUserProps = null;
let gaEnabled = false;

const KEY = import.meta.env.VITE_POSTHOG_KEY;
const HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com';
const GA_ID = import.meta.env.VITE_FIREBASE_MEASUREMENT_ID;


async function initPostHog() {
    if (phEnabled || !KEY) return;
    const mod = await import('posthog-js');
    ph = mod.default || mod.posthog || mod;
    ph.init(KEY, {
        api_host: HOST,
        person_profiles: 'identified_only',   // don't create profiles for anon noise
        capture_pageview: false,               // we fire SPA pageviews on route change
        capture_pageleave: true,               // engagement/time-on-page needs leave events
        // Autocapture records the TEXT of clicked elements ($el_text). This app
        // renders decks, scripts and reviews as ordinary DOM — masking inputs is
        // NOT enough. Strip element text from autocapture entirely so no rendered
        // content rides along; we still get the click target + structure.
        // These two are top-level PostHog options, not members of the autocapture
        // config: nested inside it they parsed and did nothing at all (security
        // review, PR #63, against @posthog/types PostHogConfig).
        mask_all_element_attributes: true,
        mask_all_text: true,
        session_recording: {
            // Replays show where users get stuck — but never their content. Mask
            // ALL rendered text + inputs, not just form fields, because the
            // sensitive data here (essays, transcripts, chat, aid) lives in divs
            // and markdown, not <input>s. This is the privacy promise in the
            // header comment; maskAllInputs alone would break it.
            maskAllInputs: true,
            maskTextSelector: '*',
            // A recording is the presenter's (ADR 0002): replay never captures a playing run.
            blockSelector: 'video, audio',
        },
        respect_dnt: true,
        disable_surveys: false,
        // posthog-js builds $current_url from window.location.href into the base properties of EVERY event,
        // not only the pageview we fire ourselves: autocapture clicks, $pageleave, $rageclick, web vitals and
        // the replay's page URL all carry it. Overriding it on our own pageview was therefore one door out of
        // many (security review, PR #63). This is the door they all pass through.
        sanitize_properties: (props) => {
            const out = { ...props };
            for (const k of ['$current_url', '$referrer', '$pathname']) {
                if (typeof out[k] === 'string' && out[k]) out[k] = scrubUrl(out[k]);
            }
            return out;
        },
    });
    // Legacy-cookie migration (founder-caught 2026-08-23). Devices identified
    // BEFORE the hashed-identity flip (2026-08-17) persist the RAW EMAIL as
    // their PostHog distinct_id. A signed-in session eventually re-identifies
    // to the u_ hash, but a signed-out or bounced-early return visit never
    // does — every pageview/pageleave/autocapture kept emitting under the
    // email (live rows in Explore sessions dated days after the flip).
    // Rebind such a cookie to its hashed id right here at init: same person,
    // no email on the wire, before any queued event replays. An INTERNAL
    // email opts out entirely — its cookie'd pageviews were re-polluting
    // funnels through the same gap.
    try {
        const cur = typeof ph.get_distinct_id === 'function' ? ph.get_distinct_id() : '';
        if (cur && String(cur).includes('@')) {
            if (INTERNAL_EMAILS.has(String(cur).trim().toLowerCase())) {
                ph.opt_out_capturing?.();
                ph.reset();
            } else {
                const { analyticsId } = await import('../utils/analyticsId');
                const id = await analyticsId(cur);
                // Cannot hash -> never keep emitting as the email: drop to a
                // fresh anonymous id instead (a person-split beats a leak).
                if (id) ph.identify(id); else ph.reset();
            }
        }
    } catch { /* never throw */ }
    phEnabled = true;
}

// Do Not Track: PostHog honors it natively (respect_dnt above); GA4 has no
// such option, so we gate its init ourselves. Without this, the privacy
// policy's "we honor your browser's Do Not Track setting" would be true for
// only one of the two named providers (reviewer-caught, compliance fix 1).
function dntEnabled() {
    try {
        return navigator.doNotTrack === '1' || window.doNotTrack === '1'
            || navigator.msDoNotTrack === '1';
    } catch { return false; }
}

async function initGA4() {
    if (gaEnabled || !GA_ID) return;
    if (dntEnabled()) return;
    // isSupported guards SSR / unsupported browsers (analytics uses cookies + IDB).
    const [{ getAnalytics, logEvent, setUserId, setUserProperties, isSupported },
        { default: app }] = await Promise.all([
        import('firebase/analytics'),
        import('../firebase'),
    ]);
    if (!(await isSupported())) return;
    ga = getAnalytics(app);       // reads measurementId from the app config
    gaLog = logEvent;
    gaSetUserId = setUserId;
    gaSetUserProps = setUserProperties;
    gaEnabled = true;
}

// Calls made BEFORE the async init resolves are queued and replayed in order
// once the providers exist. Without this, every session's first pageview, any
// mount-time track (onboarding_started), and the mobile redirect-return
// sign-in events (signed_up!) silently vanished — the dynamic import always
// loses the race against synchronous effects. Bounded so a broken init can't
// grow memory forever.
let initFinished = false;
let initPromise = null;
const pending = [];

function defer(replay) {
    if (pending.length < 200) pending.push(replay);
}

/**
 * Initialize both providers once, as early as possible. No-ops per provider
 * without its key. Called from App on mount. Idempotent (StrictMode-safe),
 * and flushes any events queued while the providers were still loading.
 */
export function initAnalytics() {
    if (!initPromise) {
        initPromise = Promise.allSettled([
            initPostHog().catch((e) => console.warn('[analytics] posthog init failed:', e?.message)),
            initGA4().catch((e) => console.warn('[analytics] ga4 init failed:', e?.message)),
        ]).then(() => {
            initFinished = true;
            for (const replay of pending.splice(0)) {
                try { replay(); } catch { /* never throw */ }
            }
        });
    }
    return initPromise;
}

// Internal accounts (audit H8, 2026-08-09). Their sessions were landing in
// the SAME PostHog project and GA4 property as real students — inflating
// every funnel in the console, and internal purchase/sign_up events plausibly
// reached Google Ads bidding. The daily digest splits internal/external
// server-side, but no console view did. Once identify() sees an internal
// email, every subsequent track()/pageview is dropped client-side.
const INTERNAL_EMAILS = new Set([
    'cvsubs@gmail.com',
]);
let internalSession = false;
export const isInternalSession = () => internalSession;

// Identity mode (compliance fix 4, 2026-08-16), driven by feature flags the
// AnalyticsListener resolves AFTER sign-in:
//   hashed              — identify with the salted pseudonymous id (u_<hex>)
//                         instead of the raw email. GA4's terms prohibit PII
//                         in the user-id; this is the fix. Fail-closed: until
//                         the flag arrives, behavior is unchanged (email).
//   internalPassthrough — TEMPORARY verification tap: lets an allowlisted
//                         internal account (stratiauser@) be captured so the
//                         hashed id can be watched arriving in the real
//                         PostHog project before any live user changes. The
//                         digest excludes internal accounts by their hashed
//                         ids too, so the tap never pollutes funnels. Off in
//                         steady state.
let identityMode = { hashed: true, internalPassthrough: false };   // hashed is the only mode this product has had (security review, PR #3)
let lastIdentifiedEmail = null;
let identifySeq = 0;   // stale async hash must never land after reset/re-identify

export function setIdentityMode(next) {
    const merged = { ...identityMode, ...next };
    const changed = merged.hashed !== identityMode.hashed
        || merged.internalPassthrough !== identityMode.internalPassthrough;
    identityMode = merged;
    // A mode arriving after identify() already ran must re-bind the session —
    // flags resolve later than auth on a cold load.
    if (changed && lastIdentifiedEmail) identify(lastIdentifiedEmail);
}

async function identifyAs(email, traits, seq) {
    let id = email;
    if (identityMode.hashed) {
        const { analyticsId } = await import('../utils/analyticsId');
        id = await analyticsId(email);
        // Hashed mode NEVER falls back to the raw email: an environment that
        // cannot hash sends no identity at all.
        if (!id) return;
    }
    if (seq !== identifySeq) return;   // superseded while hashing
    if (phEnabled && ph) { try { ph.opt_in_capturing?.(); ph.identify(id, traits); } catch { /* never throw */ } }
    if (gaEnabled && ga) { try { gaSetUserId(ga, id); } catch { /* never throw */ } }
}

/** Bind events to the signed-in user (call on auth). */
export function identify(email, traits = {}) {
    if (!email) return;
    internalSession = INTERNAL_EMAILS.has(String(email).toLowerCase());
    if (internalSession && !identityMode.internalPassthrough) {
        try { ph?.opt_out_capturing?.(); } catch { /* never throw */ }
        return;                       // never identified, never captured
    }
    lastIdentifiedEmail = email;
    if (!initFinished) { defer(() => identify(email, traits)); return; }
    identifyAs(email, traits, ++identifySeq);
}

/** Clear identity on logout so the next user isn't merged into this one. */
export function resetIdentity() {
    internalSession = false;
    lastIdentifiedEmail = null;
    identifySeq += 1;                 // invalidate any in-flight hash
    if (!initFinished) { defer(() => resetIdentity()); return; }
    if (phEnabled && ph) { try { ph.reset(); } catch { /* never throw */ } }
    if (gaEnabled && ga) { try { gaSetUserId(ga, null); } catch { /* never throw */ } }
}

/** The URL as analytics may report it — NEVER the fragment: the app doesn't
 * hash-route, and /review magic links carry a bearer SECRET in the fragment
 * (PR #854 finding — reporting location.href verbatim would ship that
 * credential to PostHog/GA4). Strip unconditionally, belt-and-braces with the
 * review page's own module-load scrub. */
// Query params that are credentials or PII and must NEVER reach an analytics
// backend: grant/token are redeemable secrets from emailed links, e is a raw
// email address (also a GA4 terms violation). Every reported URL passes
// through here (security review 2026-07-27, #4).
// oobCode/apiKey/mode/continueUrl: a Firebase email sign-in link lands on /runs
// with a live single-use code (security review, PR #12).
// page: the landing hero hands the visitor's own address to the builder as
// /app?page=..., and the route change reports a pageview before the builder has
// taken it out of the bar. Their site is theirs to tell people about, not ours
// (reviewer, PR #63).
const SENSITIVE_PARAMS = ['grant', 'token', 'e', 'oobCode', 'apiKey', 'mode', 'continueUrl', 'link', 'page'];

/** One URL, with every sensitive parameter removed. Every reported URL passes through here: the pageview we
 * fire, and, through sanitize_properties, the $current_url and $referrer that posthog-js attaches to every
 * other event by itself (security review, PR #63). */
export function scrubUrl(href) {
    const base = String(href || '').split('#')[0];
    try {
        const url = new URL(base);
        let dirty = false;
        for (const p of SENSITIVE_PARAMS) {
            if (url.searchParams.has(p)) { url.searchParams.delete(p); dirty = true; }
        }
        return dirty ? url.toString() : base;
    } catch {
        return base;
    }
}

export function reportableHref() {
    return scrubUrl(window.location.href);
}

// GA4 treats these event-parameter names as TRAFFIC SOURCE, not as dimensions:
// sending `source: 'add_college'` on any event re-attributes that whole session
// away from however the user actually arrived.
//
// We were doing exactly that. On 2026-08-04 the GA4 Session source/medium report
// listed `add_college`, `add_college_analysis`, `financial_aid_office` and
// `profile` as traffic sources — our own UI-origin labels — and ~50% of sessions
// landed in "Unassigned". Paid sessions were being overwritten the moment the
// visitor clicked a source link or added a college, so paid was UNDER-counted.
//
// Renaming happens here, at the single choke point, rather than at ~20 call
// sites: any future `source:` prop is neutralized automatically. PostHog keeps
// the original key — it has no reserved names and its history stays comparable.
const GA4_RESERVED_PARAMS = [
    'source', 'medium', 'campaign', 'term', 'content', 'campaign_id',
    'gclid', 'dclid', 'srsltid', 'source_platform', 'creative_format',
    'marketing_tactic',
];

export function ga4SafeProps(props = {}) {
    if (!props || typeof props !== 'object') return props;
    let out = null;
    for (const key of GA4_RESERVED_PARAMS) {
        if (Object.prototype.hasOwnProperty.call(props, key)) {
            out = out || { ...props };
            // Never clobber an explicit ui_* the caller already set.
            if (!Object.prototype.hasOwnProperty.call(out, `ui_${key}`)) {
                out[`ui_${key}`] = out[key];
            }
            delete out[key];
        }
    }
    return out || props;
}

/** SPA pageview — call on every route change. `props` may carry the path. */
export function pageview(path, props = {}) {
    if (internalSession) return;
    // Snapshot the URL now — by flush time the SPA may have navigated on.
    const href = reportableHref();
    if (!initFinished) { defer(() => pageview(path, { $current_url: href, ...props })); return; }
    if (phEnabled && ph) {
        try { ph.capture('$pageview', { $current_url: href, path, ...props }); } catch { /* never throw */ }
    }
    if (gaEnabled && ga) {
        try { gaLog(ga, 'page_view', ga4SafeProps({ page_path: path, page_location: href, ...props })); } catch { /* never throw */ }
    }
}

/**
 * A custom conversion event, fired to BOTH providers. `name` should come from
 * EVENTS below so the taxonomy stays consistent (see docs/analytics.md). Props
 * are dimensions — never document/essay/chat text.
 */
// GA4's event diet (compliance fix 5, 2026-08-17): Google receives ONLY the
// two Ads conversions — 'purchase' and 'sign_up', the exact pair imported
// into Google Ads (marketing/google-ads-setup.md §1.5: "Two conversions,
// ever") — plus page_view from pageview() below, which GA4's
// channel/session reporting (the digest's acquisition section) needs.
// Every product-taxonomy event (colleges added, essays, roadmap, chat...)
// is covered information in SOPIPA's sense and stays PostHog-only. This
// changes NOTHING for Ads bidding (it only ever imported these two) and
// nothing for the digest (its GA4 reads are channel dimensions).
const GA4_FORWARDED_EVENTS = new Set(['purchase', 'sign_up']);

export function track(name, props = {}) {
    if (internalSession) return;   // internal accounts never reach the funnels
    if (!name) return;
    if (!initFinished) { defer(() => track(name, props)); return; }
    if (phEnabled && ph) { try { ph.capture(name, props); } catch { /* never throw */ } }
    // GA4 event names must be snake_case, <=40 chars, [a-z0-9_] — EVENTS comply.
    if (gaEnabled && ga && GA4_FORWARDED_EVENTS.has(name)) { try { gaLog(ga, name, ga4SafeProps(props)); } catch { /* never throw */ } }
}

/**
 * A completed payment, fired as BOTH our taxonomy event (credits_purchased /
 * subscription_started) AND GA4's standard `purchase` (value + currency +
 * transaction_id) — the standard name is what Google Ads imports for
 * value-based conversion bidding, the taxonomy name is what our funnels use.
 * Caller dedupes (a refreshed success page must not double-count revenue).
 */
export function trackPurchase({ productId, value, transactionId, isSubscription }) {
    // Reference the taxonomy constants, not string literals: a 2026-07 analytics
    // audit grepped for EVENTS.CREDITS_PURCHASED, found nothing, and concluded
    // the revenue funnel was dead when it had been firing all along.
    const taxonomyEvent = isSubscription ? EVENTS.SUBSCRIPTION_STARTED : EVENTS.CREDITS_PURCHASED;
    track(taxonomyEvent, { product_id: productId, value });
    track('purchase', {
        transaction_id: transactionId,
        value,
        currency: 'USD',
        items: [{ item_id: productId, item_name: productId, price: value, quantity: 1 }],
    });
}

/**
 * Dimensions attached to every SUBSEQUENT event in this browser session.
 *
 * Distinct from setUserProps for two reasons that both bite on the landing
 * page, which is where this was introduced (landing_v2, 2026-08-15):
 *   - person properties are dropped for anonymous visitors
 *     (`person_profiles: 'identified_only'` above), and a landing-page A/B
 *     read is almost entirely anonymous;
 *   - the route's $pageview fires before an async flag read can resolve, so
 *     the dimension has to ride the events that come AFTER it — the try-hero
 *     reads, the sign_up, the purchase. Those are the funnel questions.
 *
 * Register dimensions only. Anything that is an ACTION belongs in EVENTS.
 */
// Session/person properties are PostHog-ONLY since fix 5 (2026-08-17,
// reviewer-caught): the GA4 leg was sending user properties — including
// signup_domain, which for a school-domain account IS the school — and
// the policy's "Google Analytics receives only page views and
// purchase/sign-up confirmations" must be literally true. No GA4 consumer
// reads user properties (ga4_acquisition = channel dimensions; Ads = the
// two conversions), so nothing is lost. The diet test pins this.
export function registerSessionProps(props = {}) {
    if (internalSession) return;
    if (!initFinished) { defer(() => registerSessionProps(props)); return; }
    if (phEnabled && ph) { try { ph.register(props); } catch { /* never throw */ } }
}

/** Set durable person properties (grade, plan, cohort) for segmentation. */
export function setUserProps(props = {}) {
    if (!initFinished) { defer(() => setUserProps(props)); return; }
    if (phEnabled && ph) { try { ph.setPersonProperties(props); } catch { /* never throw */ } }
}

// ---------------------------------------------------------------------------
// Event taxonomy — the single source of truth for custom event names.
// object_verb, past tense. Autocapture covers raw clicks/pageviews; these are
// the CONVERSION MILESTONES that define funnels and activation. Keep in sync
// with docs/analytics.md.
export const EVENTS = {
    // ===== platform / shell =====
    STALE_BUILD_RELOADED: 'stale_build_reloaded',   // {chunk}
    APP_ERROR_BOUNDARY: 'app_error_boundary',       // {stale_build, message, path}
    PWA_INSTALL_PROMPTED: 'pwa_install_prompted',
    PWA_INSTALL_DISMISSED: 'pwa_install_dismissed',

    // ===== landing =====
    LANDING_CTA_CLICKED: 'landing_cta_clicked',     // {cta: 'builder'|'full_run'|'brief', surface: 'hero'|'end'}
    LANDING_FILM_WATCHED: 'landing_film_watched',   // {scenes} the screens sequence ran once through (round 13)
    LANDING_PAGE_SUBMITTED: 'landing_page_submitted',  // {typed: bool} an address was handed to the builder from the hero (round 13); never the address itself
    FAQ_OPENED: 'faq_opened',                       // {question}

    // ===== writing =====
    ARTICLE_VIEWED: 'article_viewed',               // {slug}

    // ===== the builder (all local; props are counts and enums, never deck text) =====
    BUILDER_OPENED: 'builder_opened',
    DECK_BUILT: 'deck_built',                       // {slides, polls, videos, targets_total_s, example: bool}
    DECK_BUILD_FAILED: 'deck_build_failed',         // {reason: enum from the parser's message class}
    DECK_WINDOW_OPENED: 'deck_window_opened',
    PRESENTER_OPENED: 'presenter_opened',
    DECK_DOWNLOADED: 'deck_downloaded',             // {slides}
    DECK_BRIEF_SHORTCUT: 'deck_brief_shortcut',     // {left} the usual session filled the remaining answers (round 13)
    DECK_DRAFT_STARTED: 'deck_draft_started',       // {door: 'page'|'brief'} a brief was sent to be drafted (story #36)
    DECK_DRAFTED: 'deck_drafted',                   // {slides, polls, from_page: bool} a draft landed in the editor
    DECK_DRAFT_FAILED: 'deck_draft_failed',         // {reason: 'signin'|'address'|'cap'|'brief'|'checks'|'unavailable'|'server'|'network'}
    DECK_LOOK_CHOSEN: 'deck_look_chosen',           // {source: 'page'|'house', tweaked: bool} a look was picked before the draft (story #44)

    // ===== runs (ADR 0002; props are enums and size buckets, never a recording, a transcript or a number from it) =====
    SIGNIN_STARTED: 'signin_started',               // {method: 'google'|'email'}
    SIGNIN_COMPLETED: 'signin_completed',           // {method}
    RUN_UPLOAD_STARTED: 'run_upload_started',       // {source: 'studio'|'zoom', size_bucket: '<100mb'|'<500mb'|'<2gb', with_deck: bool}
    RUN_UPLOADED: 'run_uploaded',                   // {source, size_bucket}
    RUN_UPLOAD_FAILED: 'run_upload_failed',         // {reason: 'too_large'|'not_media'|'storage'|'cancelled'}
    RUN_REVIEW_VIEWED: 'run_review_viewed',         // {status: 'uploaded'|'transcribing'|'measured'|'failed', with_deck: bool}
    RUN_DELETED: 'run_deleted',
    SCRIPT_PROPOSALS_VIEWED: 'script_proposals_viewed', // {slides, proposed, prior_runs} the revised-script section rendered
    SCRIPT_APPLIED: 'script_applied',               // {accepted, proposed} accepted changes compiled into the deck and opened in the builder
    COACH_ASKED: 'coach_asked',                     // {mode: 'typed'|'spoken', answered: bool} a question about a run went to the coach
    COACH_PLAYED: 'coach_played',                   // {source: 'all'|'fix'} the spoken coach was played on the review page
    STUDIO_RUN_RECEIVED: 'studio_run_received',   // {with_deck: bool, length_bucket: '<15m'|'<45m'|'<2h'} a recording crossed from the presenter window

    // ===== rooms (ADR 0003; props are the room's id and enums, never a token, a link or a room's text) =====
    ROOM_CONNECT_STARTED: 'room_connect_started',    // {provider} the founder was sent to the room to say yes
    ROOM_CONNECTED: 'room_connected',                // {provider} the room was attached to this account
    ROOM_DISCONNECTED: 'room_disconnected',          // {provider}
    ROOM_FAILED: 'room_failed',                      // {provider, reason: 'signin'|'not_connected'|'unavailable'|'rate_limited'|'room_setup'|'bad_request'|'room'|'network'}
    SESSION_SCHEDULE_STARTED: 'session_schedule_started', // {provider}
    SESSION_SCHEDULED: 'session_scheduled',          // {provider, polls} a webinar was created from the deck
    ROOM_RUN_VIEWED: 'room_run_viewed',              // {provider} a run that came from a room was opened
};
